import {Badge, Timeline} from '@mantine/core';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {IconCheck, IconX} from '@tabler/icons-react';
import React from 'react';
import type {
  WorkflowResponse,
  WorkflowTriggerResponse} from 'src/api/ProactApiClient';
import {
  WorkflowActionStateEnum,
  WorkflowDefinitionActionTypeEnum,
  WorkflowDefinitionTriggerTypeEnum,
  WorkflowStateEnum
} from 'src/api/ProactApiClient';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {
  classifyActionState,
  classifyWorkflowState,
  formatDuration,
  getActionEntityType,
  getTimelineStateColor,
  getTriggerEntityType,
  parseUtcDate,
  phaseConfig,
  workflowEntityConfig,
} from './workflowDesignSystem';

export interface WorkflowTimelineCompactProps {
  workflow: WorkflowResponse;
  compact?: boolean;
  highlightEntityId?: {type: string; id: number};
}

function formatDate(date?: Date | null): string {
  if (!date) return '';
  return parseUtcDate(date).toLocaleString();
}

function getTriggerDescription(trigger: WorkflowTriggerResponse): string {
  const def = trigger.triggerDefinition;
  if (!def) return 'Trigger';

  switch (def.type) {
    case WorkflowDefinitionTriggerTypeEnum.Schedule:
      return `Schedule (${def.scheduleIntervalType ?? 'Unknown'})`;
    case WorkflowDefinitionTriggerTypeEnum.TaskCompletion:
      return `Task Completion: ${
        trigger.triggeredTaskAssignment?.taskDefinition?.subject ??
        def.triggerTaskDefinition?.subject ??
        'Unknown task'
      }`;
    case WorkflowDefinitionTriggerTypeEnum.FormSubmission:
      return `Form Submission: ${
        trigger.triggeredForm?.internalIdentifier ??
        def.triggerFormDefinition?.name ??
        'Unknown form'
      }`;
    case WorkflowDefinitionTriggerTypeEnum.Training:
      return `Training: ${
        trigger.triggeredTrainingSession?.courseDefinition?.name ??
        def.triggerCourseDefinition?.name ??
        'Unknown course'
      }`;
    case WorkflowDefinitionTriggerTypeEnum.ItemTracker:
      return `Item: ${
        trigger.triggeredItemAssignment?.itemDefinition?.name ?? 'Unknown item'
      }`;
    case WorkflowDefinitionTriggerTypeEnum.Manual:
      return 'Manual Trigger';
    default:
      return 'Unknown Trigger';
  }
}

function getTriggerLink(
  trigger: WorkflowTriggerResponse
): {href: string; label: string} | null {
  const def = trigger.triggerDefinition;
  if (!def) return null;

  switch (def.type) {
    case WorkflowDefinitionTriggerTypeEnum.FormSubmission:
      if (trigger.triggeredForm) {
        return {
          href: PathUtility.FormSubmission(
            trigger.triggeredForm.formDefinitionId,
            trigger.triggeredForm.id,
            'content'
          ),
          label: 'Open Form',
        };
      }
      return null;
    case WorkflowDefinitionTriggerTypeEnum.TaskCompletion:
      if (trigger.triggeredTaskAssignment?.taskDefinition) {
        return {
          href: PathUtility.TaskView(
            trigger.triggeredTaskAssignment.taskDefinition.taskType,
            trigger.triggeredTaskAssignment.taskId,
            'content'
          ),
          label: 'Open Task',
        };
      }
      return null;
    case WorkflowDefinitionTriggerTypeEnum.Training:
      if (trigger.triggeredTrainingSession) {
        return {
          href: PathUtility.TrainingCourseSessionView(
            String(trigger.triggeredTrainingSession.courseDefinitionId),
            String(trigger.triggeredTrainingSession?.assignmentId),
            String(trigger.triggeredTrainingSessionId),
            'overview'
          ),
          label: 'Open Course',
        };
      }
      return null;
    case WorkflowDefinitionTriggerTypeEnum.ItemTracker:
      if (trigger.triggeredItemAssignment) {
        return {
          href: PathUtility.ItemAssignmentView(
            trigger.triggeredItemAssignment.itemDefinitionId,
            trigger.triggeredItemAssignment.id
          ),
          label: 'Open Item',
        };
      }
      return null;
    default:
      return null;
  }
}

function isTriggerLinked(
  trigger: WorkflowTriggerResponse,
  highlight?: {type: string; id: number}
): boolean {
  if (!highlight) return false;
  const {type, id} = highlight;

  if (type === 'form' && trigger.triggeredFormId === id) return true;
  if (type === 'task' && trigger.triggeredTaskAssignment?.taskId === id)
    return true;
  if (type === 'taskAssignment' && trigger.triggeredTaskAssignmentId === id)
    return true;
  if (type === 'trainingSession' && trigger.triggeredTrainingSessionId === id)
    return true;
  if (type === 'itemAssignment' && trigger.triggeredItemAssignmentId === id)
    return true;
  return false;
}

function isActionLinked(
  action: {createdTaskDefinitionId?: number | null},
  highlight?: {type: string; id: number}
): boolean {
  if (!highlight) return false;
  return (
    highlight.type === 'task' && action.createdTaskDefinitionId === highlight.id
  );
}

export const WorkflowTimelineCompact: React.FC<
  WorkflowTimelineCompactProps
> = ({workflow, compact = false, highlightEntityId}) => {
  const activeTrigger = workflow.triggers?.find(t => t.activated);
  const sortedPhases = [...(workflow.phases ?? [])].sort((a, b) => a.id - b.id);
  const textSize = compact ? 'xs' : 'sm';
  const bulletSize = compact ? 20 : 24;
  const iconSize = compact ? 12 : 14;

  const timelineItems: React.ReactNode[] = [];
  let activeIndex = 0;
  let currentIndex = 0;

  // --- Trigger node ---
  if (activeTrigger) {
    const triggerType = getTriggerEntityType(activeTrigger);
    const entityConfig = workflowEntityConfig[triggerType];
    const TriggerIcon = entityConfig.icon;

    timelineItems.push(
      <Timeline.Item
        color="green"
        key="trigger"
        bullet={<TriggerIcon size={iconSize} />}
        title={
          <Group gap="xs">
            <Text size={textSize} fw={600}>
              Triggered
            </Text>
            <Badge size="xs" variant="light" color="green">
              Activated
            </Badge>
            {isTriggerLinked(activeTrigger, highlightEntityId) && (
              <Badge size="xs" color="green">
                Linked to Current Item
              </Badge>
            )}
          </Group>
        }>
        <Text size={textSize}>{getTriggerDescription(activeTrigger)}</Text>
        {(() => {
          const link = getTriggerLink(activeTrigger);
          return link ? (
            <Anchor href={link.href} target="_blank" size="xs">
              {link.label}
            </Anchor>
          ) : null;
        })()}
        {!compact && activeTrigger.triggeredBy && (
          <Text size="xs" c="dimmed">
            by {activeTrigger.triggeredBy.name}
          </Text>
        )}
        {!compact && (
          <Text size="xs" c="dimmed">
            {formatDate(activeTrigger.triggeredDate)}
          </Text>
        )}
      </Timeline.Item>
    );
    activeIndex = currentIndex;
    currentIndex++;
  }

  // --- Phase + action nodes ---
  for (const phase of sortedPhases) {
    const phaseState = WorkflowStateEnum[phase.state] ?? String(phase.state);
    const PhaseIcon = phaseConfig.icon;

    timelineItems.push(
      <Timeline.Item
        key={`phase-${phase.id}`}
        color={getTimelineStateColor(classifyWorkflowState(phase.state))}
        bullet={<PhaseIcon size={iconSize} />}
        title={
          <Group gap="xs">
            <Text size={textSize} fw={600}>
              {phase.name ?? `Phase ${phase.id}`}
            </Text>
            <Badge
              size="xs"
              variant="light"
              color={getTimelineStateColor(classifyWorkflowState(phase.state))}>
              {phaseState}
            </Badge>
          </Group>
        }>
        <Stack mt="sm" gap={compact ? 4 : 'xs'}>
          {phase.workflowActions?.map(action => {
            const actionState =
              WorkflowActionStateEnum[action.state] ?? String(action.state);
            const actionType = getActionEntityType(action.definition?.type);
            const actionConfig = workflowEntityConfig[actionType];
            const ActionIcon = actionConfig.icon;
            const actionTypeName =
              action.definition?.type != null
                ? WorkflowDefinitionActionTypeEnum[
                    action.definition.type
                  ].splitUppercase()
                : 'Unknown';

            return (
              <Box
                key={action.id}
                pl="sm"
                style={{borderLeft: '2px solid var(--mantine-color-gray-3)'}}>
                <Group gap="xs">
                  <ThemeIcon
                    size={compact ? 16 : 18}
                    variant="light"
                    color={actionConfig.color}
                    radius="xl">
                    <ActionIcon size={compact ? 10 : 12} />
                  </ThemeIcon>
                  <Text size={textSize}>{actionTypeName}</Text>
                  <Badge
                    size="xs"
                    variant="light"
                    color={getTimelineStateColor(
                      classifyActionState(action.state)
                    )}>
                    {actionState}
                  </Badge>
                  {isActionLinked(action, highlightEntityId) && (
                    <Badge size="xs" variant="light" color="green">
                      Linked to Current Item
                    </Badge>
                  )}
                </Group>

                {action.definition?.type ===
                  WorkflowDefinitionActionTypeEnum.CreateTask &&
                  action.createdTaskDefinition && (
                    <Box pl="md">
                      <Text size="xs">
                        {action.createdTaskDefinition.subject}
                      </Text>
                      {action.createdTaskDefinitionId &&
                        action.definition?.metadata?.createTaskMetadata
                          ?.taskType != null && (
                          <Anchor
                            href={PathUtility.TaskView(
                              action.definition.metadata.createTaskMetadata
                                .taskType,
                              action.createdTaskDefinitionId,
                              'content'
                            )}
                            target="_blank"
                            size="xs">
                            Open Task
                          </Anchor>
                        )}
                    </Box>
                  )}

                {action.definition?.type ===
                  WorkflowDefinitionActionTypeEnum.Email && (
                  <Text size="xs" c="dimmed" pl="md">
                    {action.definition.metadata?.emailMetadata?.subject ??
                      'Email'}
                  </Text>
                )}

                {!compact && action.createdAt && (
                  <Text size="xs" c="dimmed" pl="md">
                    {formatDate(action.createdAt)}
                    {action.state === WorkflowActionStateEnum.Waiting &&
                      action.createdAt && (
                        <> — Waiting {formatDuration(action.createdAt, null)}</>
                      )}
                    {action.completedAt && (
                      <>
                        {' '}
                        — Completed {formatDate(action.completedAt)} (
                        {formatDuration(action.createdAt, action.completedAt)})
                      </>
                    )}
                  </Text>
                )}
              </Box>
            );
          })}
        </Stack>
      </Timeline.Item>
    );

    if (
      phase.state === WorkflowStateEnum.Active ||
      phase.state === WorkflowStateEnum.Waiting
    ) {
      activeIndex = currentIndex;
    }
    currentIndex++;
  }

  // --- Completion/cancellation node ---
  if (workflow.state === WorkflowStateEnum.Completed && workflow.completedAt) {
    timelineItems.push(
      <Timeline.Item
        key="completed"
        color="green"
        bullet={<IconCheck size={iconSize} />}
        title={
          <Group gap="xs">
            <Text size={textSize} fw={600}>
              Completed
            </Text>
            <Badge size="xs" variant="light" color="green">
              Completed
            </Badge>
          </Group>
        }>
        {!compact && (
          <>
            <Text size="xs" c="dimmed">
              {formatDate(workflow.completedAt)}
            </Text>
            {workflow.createdAt && (
              <Text size="xs" c="dimmed">
                Total duration:{' '}
                {formatDuration(workflow.createdAt, workflow.completedAt)}
              </Text>
            )}
          </>
        )}
      </Timeline.Item>
    );
    activeIndex = currentIndex;
    currentIndex++;
  } else if (workflow.state === WorkflowStateEnum.Cancelled) {
    timelineItems.push(
      <Timeline.Item
        key="cancelled"
        color="red"
        bullet={<IconX size={iconSize} />}
        title={
          <Group gap="xs">
            <Text size={textSize} fw={600}>
              Cancelled
            </Text>
            <Badge size="xs" variant="light" color="red">
              Cancelled
            </Badge>
          </Group>
        }>
        {!compact && (
          <Text size="xs" c="dimmed">
            {formatDate(workflow.updatedAt)}
          </Text>
        )}
      </Timeline.Item>
    );
    activeIndex = currentIndex;
  }

  return (
    <Stack gap={compact ? 'xs' : 'sm'}>
      {!compact && (
        <Text fw={600} size="lg">
          Execution Timeline
        </Text>
      )}
      <Timeline active={activeIndex} bulletSize={bulletSize} lineWidth={2}>
        {timelineItems}
      </Timeline>
    </Stack>
  );
};
